import * as React from 'react';
import { trackEvent } from '../analytics';
import { useCurrentDate } from '../hooks/use-current-date';
import { getNumberParts, parseNumber } from '../lib';
import { DateField } from './date-field';
import { useLocalize } from './localize-context';
import { MessageBtn } from './message-btn';
import { TextField } from './text-field';
import { VisitorContext } from './visitor-context';
import { VisitorTypeSelect } from './visitor-type-select';

type CustomProductBookingFormProps = {
  productName: string;
  productUid: string;
  items: Array<{
    description: {
      text: string | null;
    };
    subtext: {
      text: string | null;
    };
    price: number;
    unit: {
      text: string | null;
    };
  }>;
};

export const CustomProductBookingForm = (
  props: CustomProductBookingFormProps
) => {
  const localize = useLocalize();
  const today = useCurrentDate();
  const [visitorType] = React.useContext(VisitorContext);
  const [date, setDate] = React.useState('');
  const [values, setValues] = React.useState<string[]>([]);
  React.useEffect(() => {
    setValues(createEmptyArrayofLength(props.items.length));
  }, [props.items.length]);
  const valsInNumber = values.map(parseNumber);
  const total = valsInNumber.reduce((result, value, index) => {
    const itemDetails = props.items[index];
    if (!itemDetails) {
      return result;
    }
    return result + value * itemDetails.price;
  }, 0);
  const [totalPrice, decimals] = getNumberParts(total);
  const whatsappMsg = `${localize('Service')}: ${props.productName}
${localize('Date')}: ${date}
${localize(visitorType === 'foreigner' ? 'Foreigner' : 'Malaysian')}
${values.map((value, index) => {
  const itemDetails = props.items[index];
  if (!itemDetails) {
    return '';
  }
  return (
    `${itemDetails.description.text} ${
      itemDetails.subtext.text ? `${itemDetails.subtext.text}` : ''
    } X ${value}` + '\n'
  );
})}
${localize('Total')}: ${totalPrice}.${decimals}`;

  return (
    <div>
      <div className="space-y-4 md:flex md:space-x-2 md:space-y-0 mb-4">
        <DateField
          value={date}
          onChangeValue={setDate}
          aria-label={localize('Date')}
          placeholder={localize('Date')}
          minDate={today}
        />
        <VisitorTypeSelect wrapperClassName="flex-1" />
      </div>
      {values.map((value, index) => {
        const itemDetails = props.items[index];

        if (!itemDetails) {
          return null;
        }

        return (
          <TextField
            value={value}
            type="number"
            step={1}
            min={0}
            onChangeValue={newValue =>
              setValues(oldValues =>
                oldValues.map((val, i) => (i === index ? newValue : val))
              )
            }
            append={
              <>
                {itemDetails.description.text}
                {itemDetails.subtext.text && (
                  <>
                    <br />
                    {itemDetails.subtext.text}
                  </>
                )}
              </>
            }
            placeholder={itemDetails.unit.text || undefined}
            appendWidth="half"
            wrapperClassName="flex-1 mb-4"
            key={index}
          />
        );
      })}
      <div className="pt-3 xs:flex justify-between items-center">
        <output className="block mb-2">
          <small className="text-2xl mr-1">{localize('RM')}</small>
          <span className="text-4xl">{totalPrice}</span>
          <small className="text-2xl">.{decimals}</small>
        </output>
        <div className="text-right">
          <MessageBtn
            message={whatsappMsg}
            onClick={() => {
              trackEvent({
                category: 'button',
                action: 'click',
                label: `order ${props.productUid}`,
                value: totalPrice,
              });
            }}
            disabled={!date || !total}
          />
        </div>
      </div>
    </div>
  );
};

const createEmptyArrayofLength = (length: number) => {
  const result: string[] = [];

  for (let x = 0; x < length; x++) {
    result.push('');
  }

  return result;
};
